import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getAdminProfessionals,
  getAdminProfessionalById,
  verifyProfessional,
  resendCredentials,
  suspendProfessional,
  reactivateProfessional,
  forceProfessionalOffline,
  clearProfessionalForcedOffline,
  issueCredentials,
  requestChanges,
  reopenProfessional,
  updateProfessionalRate,
  type AdminProfessionalQueryParams,
} from '../services/professional-admin.service';
import { extractErrorMessage } from '@/lib/api';

export function useAdminProfessionals(params?: AdminProfessionalQueryParams) {
  return useQuery({
    queryKey: ['professionals', 'admin', params],
    queryFn: () => getAdminProfessionals(params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminProfessionalById(id: string) {
  return useQuery({
    queryKey: ['professionals', 'admin', id],
    queryFn: () => getAdminProfessionalById(id),
    enabled: !!id,
  });
}

export function useVerifyProfessional() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { verificationStatus: 'verified' | 'rejected'; rejectionReason?: string; adminNotes?: string } }) =>
      verifyProfessional(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
  });
}

export function useResendCredentials() {
  return useMutation({
    mutationFn: (id: string) => resendCredentials(id),
    onSuccess: () => {
      toast.success('Credenziali di accesso re-inviate.');
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err));
    },
  });
}

export function useSuspendProfessional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      suspendProfessional(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
  });
}

export function useReactivateProfessional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivateProfessional(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
  });
}

export function useForceProfessionalOffline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      forceProfessionalOffline(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
  });
}

export function useClearForcedOffline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clearProfessionalForcedOffline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
  });
}

export function useIssueCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => issueCredentials(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Credenziali consegnate al professionista.');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useRequestChanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => requestChanges(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Modifiche richieste al professionista.');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useReopenProfessional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenProfessional(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Profilo riportato in lavorazione.');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useUpdateProfessionalRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, costPerMinute }: { id: string; costPerMinute: number }) =>
      updateProfessionalRate(id, costPerMinute),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Tariffa al minuto aggiornata.');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}
